// RazorpayButton.jsx
import React from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";
import { useState } from "react";

const RazorpayButton = ({
  amount,
  isValid,
  onBeforePay,
  loading,
  currency,
  user,
  buttonText = "Pay Now",
  onSuccess,
  styleClass,
  RazorpayApiKey,
  RazorPaySecret,
  participantDetails,
  eventId,
  eventName,
  orderMetadata,
  useCouponCode,
  couponDiscount,
  backgroundImage
}) => {
  // ✅ NEW: Add processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const loadRazorpay = async (e) => {
    e.preventDefault();
    
    if (onBeforePay && !onBeforePay()) {
      toast.dismiss();
      toast.error("Please fill all the fields");
      return;
    }

    try {
      const orderUrl = `${process.env.REACT_APP_API_URL}/api/payment/create-order`;
      
      // ✅ Send participant details with order creation
      const { data } = await axios.post(orderUrl, { 
        amount, 
        currency,
        RazorPayKey: RazorpayApiKey,
        RazorPaySecret: RazorPaySecret,
        // NEW: Include participant data
        participantsData: participantDetails,
        eventId: eventId,
        eventName: eventName,
        orderMetadata: orderMetadata,
        useCouponCode: useCouponCode,
        couponDiscount: couponDiscount,
        backgroundImage: backgroundImage
      });

      const options = {
        key: RazorpayApiKey,
        amount: data.amount,
        currency: data.currency,
        name: "Ticket Payment",
        description: "Event Ticket Payment",
        order_id: data.id,
        redirect: false,
        handler: async function (response) {
          console.log("✅ Payment success:", response);
 // ✅ Show processing modal
          setIsProcessing(true);
          setProcessingStep('Verifying payment...');
          try {
            // ✅ Call verification endpoint that creates participants
            const verifyResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/payment/verify-and-create-participants`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }
            );

            console.log("✅ Participants created:", verifyResponse.data);
 // ✅ Hide processing modal
            setIsProcessing(false);
            // Call success callback if provided
            if (onSuccess) {
              await onSuccess(verifyResponse.data);
            }

            Swal.fire({
              icon: 'success',
              title: 'Payment Successful!',
              text: `${verifyResponse.data.count} participant(s) registered successfully`,
            });

          } catch (verifyError) {
            console.error("❌ Verification failed:", verifyError);
            Swal.fire({
              icon: 'error',
              title: 'Verification Failed',
              text: 'Payment received but participant creation failed. Please contact support.',
            });
          }
        },
        
        prefill: {
          name: user?.firstName || participantDetails[0]?.firstName || "User",
          email: user?.email || participantDetails[0]?.email || "",
          contact: user?.phone || participantDetails[0]?.phone || "",
        },
        
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (err) {
      console.error("Razorpay load failed:", err);
      toast.error("Failed to initiate payment");
    }
  };

  return (
    <>
    
    <button onClick={loadRazorpay} className={styleClass} disabled={loading}>
      {loading ? "Loading..." : buttonText}
    </button>
     {/* ✅ NEW: Processing Modal */}
      {isProcessing && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
                <svg 
                  className="w-8 h-8 text-white animate-spin" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  ></circle>
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Processing Registration
              </h3>
              <p className="text-gray-600">
                Please wait while we complete your registration...
              </p>
            </div>

            {/* Progress Steps */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Payment Verified</p>
                  <p className="text-xs text-gray-500">Your payment was successful</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {processingStep.includes('Creating') || processingStep.includes('Generating') || processingStep.includes('Sending') || processingStep.includes('Finalizing') ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Creating Records</p>
                  <p className="text-xs text-gray-500">Setting up your participant profile</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {processingStep.includes('Generating') || processingStep.includes('Sending') || processingStep.includes('Finalizing') ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Generating ID Card</p>
                  <p className="text-xs text-gray-500">Creating your personalized ID card</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {processingStep.includes('Sending') || processingStep.includes('Finalizing') ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Sending Confirmation</p>
                  <p className="text-xs text-gray-500">Emailing your ID card and details</p>
                </div>
              </div>
            </div>

            {/* Current Step Indicator */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <p className="text-sm font-medium text-blue-600">
                  {processingStep}
                </p>
              </div>
            </div>

            {/* Warning */}
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800 text-center">
                ⚠️ Please do not close this window or refresh the page
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RazorpayButton;
